import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { LatLngTuple, Icon, divIcon } from 'leaflet';
import { Search, GitCompare } from 'lucide-react';
import {
  Location,
  LocationCategory,
  ParkAndRideLocation,
  BicycleParkingLocation,
  MobilityParkingLocation,
  CarShareBayOperatorLocation,
  CostcoLocation,
} from './types';
import LocationModal from './components/LocationModal';
import BeachModal from './components/BeachModal';
import ParkAndRideModal from './components/ParkAndRideModal';
import BicycleParkingPopup from './components/BicycleParkingPopup';
import MobilityParkingPopup from './components/MobilityParkingPopup';
import CarShareBayOperatorPopup from './components/CarShareBayOperatorPopup';
import CostcoModal from './components/CostcoModal';
import BottomNavigation from './components/BottomNavigation';
import { eventLogger, getData } from './utils/auth';
import './styles/App.css';
import RecommendationsFAB from './components/RecommendationsFAB';
import ComingSoonModal from './components/ComingSoonModal';
import CrowdLevelModal from './components/recommendations/CrowdLevelModal';
import CoffeePriceModal from './components/recommendations/CoffeePriceModal';
import VibeLevelModal from './components/recommendations/VibeLevelModal';
import AddParkingRecsModal from './components/recommendations/AddParkingRecsModal';
import BeerPriceModal from './components/recommendations/BeerPriceModal';
import MyVibeModal from './components/MyVibeModal';
import Home2 from './components/Home2/Home2';
import BrandLocationsView from './components/Home2/BrandLocationsView';
import LocationsListView from './components/Home2/LocationsListView';
import BrandsListView from './components/Home2/BrandsListView';
import AllSearch from './components/AllSearch';
import { testRecommendation } from './utils/recommendations';

// Data imports
import costcoData from './assets/costco.json';
import beachesData from './assets/beaches.json';
import bicycleParkingData from './assets/Bicycle_parking_-3619872741289052610.json';
import mobilityParkingData from './assets/Mobility_parking.json';
import carShareBayOperatorData from './assets/Car_share_bay_operator.json';
import { m } from 'framer-motion';
import LocationComparer from './components/LocationComparer';
import LocationComparer2 from './components/LocationComparer2';
import CoffeeMapper2 from './components/CoffeeMapper2';
import MapPage from './components/pages/MapPage/MapPage';
import FeedbackPage from './components/pages/FeedbackPage/FeedbackPage';
import { preprocessLocation } from './utils/coffeeFilterMatcherV4';
import { preprocessLocationSizes } from './utils/sizeNormalizer';

// Custom hook for map interactions
const MapController: React.FC<{
  onLocationSelect: (location: Location) => void;
  selectedCategory: LocationCategory | null;
  mapRef: React.MutableRefObject<any>;
}> = ({ onLocationSelect, selectedCategory, mapRef }) => {
  const map = useMap();

  // Store map reference for external control
  useEffect(() => {
    if (map && mapRef) {
      mapRef.current = map;
    }
  }, [map, mapRef]);

  useEffect(() => {
    if (selectedCategory) {
      // Filter map to show only locations of selected category
      // This would integrate with your backend filtering
    }
  }, [selectedCategory, map]);

  return null;
};

const App: React.FC = () => {
  const [showLocation, setShowLocation] = useState(false);
  const [showBeach, setShowBeach] = useState(false);
  const [showParkAndRide, setShowParkAndRide] = useState(false);
  const [showBicycleParking, setShowBicycleParking] = useState(false);
  const [showMobilityParking, setShowMobilityParking] = useState(false);
  const [showCarShareBayOperator, setShowCarShareBayOperator] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [showLocationComparer, setShowLocationComparer] = useState(false);
  const [showLocationComparer2, setShowLocationComparer2] = useState(false);
  const [showCostco, setShowCostco] = useState(false);
  const [currentDetails, setCurrentDetails] = useState<Location | null>(null);
  const [currentBeach, setCurrentBeach] = useState<any>(null);
  const [currentParkAndRide, setCurrentParkAndRide] = useState<ParkAndRideLocation | null>(null);
  const [currentBicycleParking, setCurrentBicycleParking] = useState<BicycleParkingLocation | null>(null);
  const [currentMobilityParking, setCurrentMobilityParking] = useState<MobilityParkingLocation | null>(null);
  const [currentCarShareBayOperator, setCurrentCarShareBayOperator] = useState<CarShareBayOperatorLocation | null>(
    null
  );
  const [currentShop, setCurrentShop] = useState<{
    name: string;
    centreName?: string;
    latitude?: number;
    longitude?: number;
  } | null>(null);
  const [currentCostco, setCurrentCostco] = useState<CostcoLocation | null>(null);
  const [allLocations, setAllLocations] = useState({});
  const [allCoffeeLocations, setAllCoffeeLocations] = useState<Record<string, any>>({});
  const [preprocessedCoffeeLocations, setPreprocessedCoffeeLocations] = useState<Record<string, any>>({});
  const [allSearchLocations, setAllSearchLocations] = useState<string[]>([]);
  const [showBrandLocations, setShowBrandLocations] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedBrandLocations, setSelectedBrandLocations] = useState<Array<Location & { distance?: number }>>([]);
  const [showLocationsList, setShowLocationsList] = useState(false);
  const [locationsListType, setLocationsListType] = useState<'shopping' | 'beaches' | 'coffee'>('shopping');
  const [locationsListData, setLocationsListData] = useState<Array<Location & { distance?: number }>>([]);
  const [locationsListReferenceLocation, setLocationsListReferenceLocation] = useState<[number, number] | null>(null);
  const [showBrandsList, setShowBrandsList] = useState(false);
  const [brandsListReferenceLocation, setBrandsListReferenceLocation] = useState<[number, number] | null>(null);
  const [home2ReferenceLocation, setHome2ReferenceLocation] = useState<[number, number] | null>(null);
  const [coffeeLocationToOpen, setCoffeeLocationToOpen] = useState<any | null>(null);
  const [showAllSearch, setShowAllSearch] = useState(false);
  const [addToComparerCallback, setAddToComparerCallback] = useState<((id: string) => Promise<void>) | null>(null);
  const [parkAndRideLocations, setParkAndRideLocations] = useState<ParkAndRideLocation[]>([]);
  const [bicycleParkingLocations, setBicycleParkingLocations] = useState<BicycleParkingLocation[]>([]);
  const [mobilityParkingLocations, setMobilityParkingLocations] = useState<MobilityParkingLocation[]>([]);
  const [carShareBayOperatorLocations, setCarShareBayOperatorLocations] = useState<CarShareBayOperatorLocation[]>([]);
  const [costcoLocations, setCostcoLocations] = useState<CostcoLocation[]>([]);
  const [allMarkers, setAllMarkers] = useState<React.ReactNode[]>([]);
  const [isSearchOverlayActive, setIsSearchOverlayActive] = useState(false);
  const [parkAndRideMarkers, setParkAndRideMarkers] = useState<React.ReactNode[]>([]);
  const [bicycleParkingMarkers, setBicycleParkingMarkers] = useState<React.ReactNode[]>([]);
  const [mobilityParkingMarkers, setMobilityParkingMarkers] = useState<React.ReactNode[]>([]);
  const [carShareBayOperatorMarkers, setCarShareBayOperatorMarkers] = useState<React.ReactNode[]>([]);
  const [costcoMarkers, setCostcoMarkers] = useState<React.ReactNode[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<LocationCategory | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Map URL path to internal tab key
  const pathToTab: Record<string, string> = {
    '/home': 'home2',
    '/coffee': 'coffee',
    '/shops': 'home',
    '/feedback': 'feedback',
  };
  const tabToPath: Record<string, string> = {
    home2: '/home',
    coffee: '/coffee',
    home: '/shops',
    feedback: '/feedback',
  };
  const activeTab = pathToTab[location.pathname] || 'home2';

  // Redirect unknown paths to /home
  useEffect(() => {
    if (!pathToTab[location.pathname]) {
      navigate('/home', { replace: true });
    }
  }, [location.pathname]);

  const setActiveTab = useCallback(
    (tab: string) => {
      const path = tabToPath[tab] || '/home';
      if (location.pathname !== path) {
        navigate(path);
      }
    },
    [navigate, location.pathname]
  );

  const [mapCenter, setMapCenter] = useState<LatLngTuple>([-33.8688, 151.2093]); // Sydney CBD
  const [mapZoom, setMapZoom] = useState(12);
  const [mapRef] = useState(useRef<any>(null));

  // Prevent duplicate Firebase calls during clustering/marker interactions
  const locationDetailsCacheRef = useRef<Map<string, any>>(new Map());
  const pendingLocationRequestsRef = useRef<Set<string>>(new Set());
  const hasFetchedDataRef = useRef(false);

  // Current location functionality - single source of truth app-wide
  const [userLocation, setUserLocation] = useState<LatLngTuple | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [userLocationMarker, setUserLocationMarker] = useState<React.ReactNode | null>(null);
  const hasPromptedForLocationRef = useRef(false);

  // Recommendations system
  const [selectedRecommendationCategory, setSelectedRecommendationCategory] = useState<
    'parking' | 'coffee' | 'crowd' | 'vibe' | 'beer' | null
  >(null);
  const [showAddParkingModal, setShowAddParkingModal] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showMyVibe, setShowMyVibe] = useState(false);
  const [showCrowdModal, setShowCrowdModal] = useState(false);
  const [selectedCrowdLevel, setSelectedCrowdLevel] = useState<'low' | 'moderate' | 'high' | 'very high' | null>(null);
  const [showCoffeeModal, setShowCoffeeModal] = useState(false);
  const [showVibeModal, setShowVibeModal] = useState(false);
  const [selectedVibeLevel, setSelectedVibeLevel] = useState<any>(null);
  const [showBeerModal, setShowBeerModal] = useState(false);
  const [recommendationContext, setRecommendationContext] = useState<{
    name: string | null;
    coords: LatLngTuple | null;
  }>({ name: null, coords: null });
  const [showRecommendationGlow, setShowRecommendationGlow] = useState(false);

  // Check URL params to hide navbar
  const [shouldHideNavbar, setShouldHideNavbar] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const native = params.get('native');
    const nid = params.get('nid');

    if (native === 'true' && nid === '4d964d8d-6fdc-42df-b706-ef61cb73310e') {
      setShouldHideNavbar(true);
    }
  }, []);

  // MARKERS BEGIN HERE
  const createAllMarkers = useCallback(
    (locations: any[], type: string, backgroundColor: string, handleClick, setMarkers) => {
      const markers = Object.keys(locations)
        .filter((name) => {
          // Filter out locations without valid coordinates
          const location = locations[name];
          if (!location) return false;
          const lat = location.latitude;
          const lng = location.longitude;
          return lat !== undefined && lat !== null && !isNaN(lat) && lng !== undefined && lng !== null && !isNaN(lng);
        })
        .map((name) => {
          let icon = '';
          let isCostco = false;
          let isWestfield = false;
          let isBeach = false;
          if (type === 'basic') {
            if (typeof name === 'string' && name.toLowerCase().includes('costco')) {
              isCostco = true;
              icon = 'C';
            } else if (typeof name === 'string' && name.toLowerCase().includes('westfield')) {
              isWestfield = true;
              icon = 'W';
            } else if (typeof name === 'string' && beachesData[name] !== undefined) {
              isBeach = true;
              icon = `<span style="font-size: 20px; display: block; line-height: 1;">🏖️</span>`;
            } else {
              // Use shopping bag icon SVG for regular locations
              icon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display: block; margin: 0 auto;">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>`;
            }
          } else if (type === 'pnr') icon = 'P&R';
          else if (type === 'bicycle') icon = '🚲';
          else if (type === 'mobility') icon = '🦽';
          else if (type === 'carshare') icon = '🚗';

          // Determine marker background color
          let markerBackground = backgroundColor;
          if (isCostco) {
            markerBackground = 'linear-gradient(135deg, #3b82f6, #2563eb)'; // Blue for Costco
          } else if (isWestfield) {
            markerBackground = 'linear-gradient(135deg, #ef4444, #dc2626)'; // Red for Westfield
          } else if (isBeach) {
            markerBackground = 'linear-gradient(135deg, #06b6d4, #0891b2)'; // Cyan for Beaches
          }

          // Create custom circular icon with color and initials/icon
          const isShopIcon = !isCostco && !isWestfield && !isBeach && type === 'basic';
          const customIcon = divIcon({
            html: `
        <div class="custom-marker" style="
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${markerBackground};
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: ${isWestfield || isCostco ? '16px' : '12px'};
          cursor: pointer;
          transition: all 0.2s;
          ${isShopIcon ? 'padding: 0;' : ''}
          ${isBeach ? 'padding: 2px;' : ''}
        ">
          ${icon}
        </div>
      `,
            className: 'custom-marker-container',
            iconSize: [40, 40],
            iconAnchor: [20, 20],
          });

          const prop = type === 'basic' ? { _id: name, ...locations[name] } : name;
          const location = locations[name];

          return (
            <Marker
              key={name}
              position={[location.latitude, location.longitude]}
              eventHandlers={{
                click: async () => handleClick(prop),
              }}
              icon={customIcon}
            />
          );
        });
      setMarkers(markers);
    },
    []
  );

  const createUserLocationMarker = useCallback((location: LatLngTuple) => {
    const customIcon = divIcon({
      html: `
        <div class="user-location-marker" style="
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          text-align: center;
          line-height: 1;
          font-size: 12px;
          position: relative;
          animation: userLocationPulse 2s infinite;
        ">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            <circle cx="12" cy="9" r="2.5"/>
          </svg>
        </div>
      `,
      className: 'user-location-marker-container',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    const marker = <Marker key="user-location" position={location} icon={customIcon} />;

    setUserLocationMarker(marker);
  }, []);

  // MARKERS END HERE

  useEffect(() => {
    eventLogger('location_tab', { location: 'Main menu', tab: activeTab });
  }, [activeTab]);

  const generateSearchLocations = () => {
    const allOptions = [];
    Object.keys(allLocations).forEach((key) => {
      const location = allLocations[key];
      // Only process if location is an object
      if (location && typeof location === 'object' && !Array.isArray(location)) {
        allOptions.push(key);
        if ('shops' in location && Array.isArray(location.shops)) {
          allOptions.push(...location.shops.map((shop) => `${key} ::: ${shop}`));
        }
      }
    });
    setAllSearchLocations(allOptions);
    return allOptions;
  };

  const getBusynessColor = (busyness: number): string => {
    if (busyness < 30) return '#4CAF50'; // Green - Low busyness
    if (busyness < 60) return '#FF9800'; // Orange - Medium busyness
    if (busyness < 80) return '#FF5722'; // Red-Orange - High busyness
    return '#F44336'; // Red - Very high busyness
  };

  useEffect(() => {
    generateSearchLocations();
  }, [allLocations]);

  useEffect(() => {
    if (hasFetchedDataRef.current) return;
    hasFetchedDataRef.current = true;

    // Fetch regular locations
    const fetchLocations = async () => {
      try {
        const data = await getData('/locationslight');
        setAllLocations(data);
        createAllMarkers(data, 'basic', '#7c3aed', handleMarkerClick, setAllMarkers);
      } catch (error) {
        console.error('❌ Error fetching regular locations:', error);
        setAllLocations([]);
        setAllMarkers([]);
      }
    };

    // Fetch coffee locations
    const fetchCoffeeLocations = async () => {
      try {
        const data = await getData('/coffeelocationslight');
        setAllCoffeeLocations(data || {});

        // Preprocess locations immediately after fetching (to avoid lag when CoffeeMapper2 mounts)
        if (data && typeof data === 'object') {
          const preprocessed: Record<string, any> = {};
          Object.entries(data).forEach(([name, location]: [string, any]) => {
            // First preprocess for type/category matching
            const typePreprocessed = preprocessLocation(location);
            // Then preprocess for size normalization
            const fullyPreprocessed = preprocessLocationSizes(typePreprocessed);
            preprocessed[name] = fullyPreprocessed;
          });
          setPreprocessedCoffeeLocations(preprocessed);
        } else {
          setPreprocessedCoffeeLocations({});
        }
      } catch (error) {
        console.error('❌ Error fetching coffee locations:', error);
        setAllCoffeeLocations({});
        setPreprocessedCoffeeLocations({});
      }
    };

    // Fetch Park and Ride locations
    const fetchParkAndRide = async () => {
      try {
        return setParkAndRideMarkers([]);
        const response = await fetch('/api/park-and-ride');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        setParkAndRideLocations(data);
        // createParkAndRideMarkers(data);
      } catch (error) {
        console.error('❌ Error fetching Park and Ride locations:', error);
        setParkAndRideLocations([]);
        setParkAndRideMarkers([]);
      }
    };

    // Fetch Bicycle Parking locations
    const fetchBicycleParking = async () => {
      try {
        // TODO: Change this to filter instead of limiting to 100
        const data = bicycleParkingData.features.slice(0, 100).map((feature: any) => ({
          id: feature.properties.OBJECTID,
          assetId: feature.properties.AssetID,
          type: feature.properties.Type,
          streetName: feature.properties.StreetName,
          suburb: feature.properties.Suburb,
          postcode: feature.properties.Postcode,
          latitude: feature.geometry.coordinates[1],
          longitude: feature.geometry.coordinates[0],
        }));
        setBicycleParkingLocations(data);
        createAllMarkers(
          data,
          'bicycle',
          'linear-gradient(135deg, #4CAF50, #45a049)',
          handleBicycleParkingClick,
          setBicycleParkingMarkers
        );
      } catch (error) {
        console.error('❌ Error fetching Bicycle Parking locations:', error);
        setBicycleParkingLocations([]);
        setBicycleParkingMarkers([]);
      }
    };

    // Fetch Mobility Parking locations
    const fetchMobilityParking = async () => {
      try {
        // TODO: Change this to filter instead of limiting to 100
        const data = mobilityParkingData.features.slice(0, 100).map((feature: any) => ({
          objectId: feature.properties.OBJECTID,
          address: feature.properties.Address,
          street: feature.properties.Street,
          location: feature.properties.Location,
          suburb: feature.properties.Suburb,
          sideOfStreet: feature.properties.SideOfStreet,
          numberParkingSpaces: feature.properties.NumberParkingSpaces,
          parkingSpaceWidth: feature.properties.ParkingSpaceWidth,
          parkingSpaceLength: feature.properties.ParkingSpaceLength,
          parkingSpaceAngle: feature.properties.ParkingSpaceAngle,
          signText: feature.properties.SignText,
          url: feature.properties.URL,
          auditDate: feature.properties.AuditDate,
          latitude: feature.geometry.coordinates[1],
          longitude: feature.geometry.coordinates[0],
        }));

        setMobilityParkingLocations(data);
        createAllMarkers(
          data,
          'mobility',
          'linear-gradient(135deg, #3B82F6, #2563EB)',
          handleMobilityParkingClick,
          setMobilityParkingMarkers
        );
      } catch (error) {
        console.error('❌ Error fetching Mobility Parking locations:', error);
        setMobilityParkingLocations([]);
        setMobilityParkingMarkers([]);
      }
    };

    // Fetch Car Share Bay Operator locations
    const fetchCarShareBayOperator = async () => {
      try {
        const data = carShareBayOperatorData.features.map((feature: any) => ({
          objectId: feature.properties.OBJECTID,
          bayId: feature.properties.Bay_ID,
          operator: feature.properties.Operator,
          street: feature.properties.Street,
          suburb: feature.properties.Suburb,
          latitude: feature.geometry.coordinates[1],
          longitude: feature.geometry.coordinates[0],
        }));

        setCarShareBayOperatorLocations(data);
        createAllMarkers(
          data,
          'carshare',
          'linear-gradient(135deg, #9C27B0, #7B1FA2)',
          handleCarShareBayOperatorClick,
          setCarShareBayOperatorMarkers
        );
      } catch (error) {
        console.error('❌ Error fetching Car Share Bay Operator locations:', error);
        setCarShareBayOperatorLocations([]);
        setCarShareBayOperatorMarkers([]);
      }
    };

    // Load all data sources
    fetchLocations();
    fetchCoffeeLocations();
    fetchParkAndRide();
    //fetchBicycleParking();
    //fetchMobilityParking();
    //fetchCarShareBayOperator();

    // Initially show only regular locations (hide Park and Ride and Bicycle Parking)
    // They will only show when their respective categories are selected
    setTimeout(() => {
      setAllMarkers([]);
    }, 100);
  }, []);

  // Initial marker creation - only show regular locations and park & ride by default
  // useEffect(() => {
  //   if (allLocations.length > 0) {
  //     createAllMarkers(allLocations, "basic", "#3B82F6", handleMarkerClick, setAllMarkers);
  //   }
  //   if (parkAndRideLocations.length > 0) {
  //     createAllMarkers(parkAndRideLocations, "pnr", "#3B82F6", handleParkAndRideClick, setParkAndRideMarkers);
  //   }
  //   // Don't show bicycle parking and mobility parking by default
  //   createAllMarkers([], "bicycle", "linear-gradient(135deg, #4CAF50, #45a049)", handleBicycleParkingClick, setBicycleParkingMarkers);
  // }, [allLocations, parkAndRideLocations]);

  const getParkAndRideDetails = async (facilityId: string) => {
    try {
      // For now, return the data from the already loaded parkAndRideLocations
      // In the future, this could fetch real-time data from the API
      const location = parkAndRideLocations.find((loc) => loc.facility_id === facilityId);
      if (location) {
        return location;
      }
      throw new Error('Park and Ride location not found');
    } catch (error) {
      console.error('Error fetching Park and Ride details:', error);
      return null;
    }
  };

  const getBicycleParkingDetails = async (facilityId: number) => {
    try {
      // For now, return the data from the already loaded bicycleParkingLocations
      // In the future, this could fetch real-time data from the API
      const location = bicycleParkingLocations.find((loc) => loc.id === facilityId);
      if (location) {
        return location;
      }
      throw new Error('Bicycle Parking location not found');
    } catch (error) {
      console.error('Error fetching Bicycle Parking details:', error);
      return null;
    }
  };

  const getMobilityParkingDetails = async (facilityId: number) => {
    try {
      // For now, return the data from the already loaded mobilityParkingLocations
      // In the future, this could fetch real-time data from the API
      const location = mobilityParkingLocations.find((loc) => loc.objectId === facilityId);
      if (location) {
        return location;
      }
      throw new Error('Mobility Parking location not found');
    } catch (error) {
      console.error('Error fetching Mobility Parking details:', error);
      return null;
    }
  };

  const getCarShareBayOperatorDetails = async (facilityId: number) => {
    try {
      // For now, return the data from the already loaded carShareBayOperatorLocations
      // In the future, this could fetch real-time data from the API
      const location = carShareBayOperatorLocations.find((loc) => loc.objectId === facilityId);
      if (location) {
        return location;
      }
      throw new Error('Car Share Bay Operator location not found');
    } catch (error) {
      console.error('Error fetching Car Share Bay Operator details:', error);
      return null;
    }
  };

  const handleMarkerClick = async (location: any, shop: any = null, details: any = null) => {
    // console.log('📍 Marker clicked:', location, shop);
    let locationDetails = details;
    const isBeachLocation = location?.category === 'Beaches';

    // For beaches, use the location object directly (it was spread from allLocations)
    if (!details || (details && shop)) {
      // if we only get shop details, we need to fetch parent location still
      try {
        locationDetails = await getData(`/locations/${location._id}`);
      } catch (error) {
        // If fetch fails and we have the location in allLocations, use that
        if (allLocations[location._id]) {
          locationDetails = allLocations[location._id];
        }
      }
    }

    if (shop) setCurrentShop(shop);
    else setCurrentShop(null);

    // console.log('📋 Fetched location details:', locationDetails);

    // Check if location is a Beach
    if (isBeachLocation) {
      const additionalBeachDetails = beachesData[location._id];

      const beachDetails = {
        _id: location._id,
        name: location._id,
        details: locationDetails,
        beachData: additionalBeachDetails.beachData, // Use beachData from locationDetails
      };

      setCurrentBeach(beachDetails);
      setShowBeach(true);
      setRecommendationContext({
        name: location._id,
        coords: [locationDetails.latitude, locationDetails.longitude] as LatLngTuple,
      });
      // Center the map on the selected location with smooth animation
      centerMapOnLocation(locationDetails.latitude, locationDetails.longitude, 16);
      return;
    }
    // Check if location is a Costco (by name or by costcoData match)
    else if (
      (typeof location._id === 'string' && location._id.toLowerCase().includes('costco')) ||
      location._id in costcoData
    ) {
      // Use costcoData if available, otherwise create from location data
      const costcoDataEntry = costcoData[location._id] || {};

      let currentCostco: CostcoLocation = {
        store_id: costcoDataEntry.store_id || location._id,
        name: costcoDataEntry.name || location._id,
        address: costcoDataEntry.address || locationDetails.address || '',
        latitude: locationDetails.latitude,
        longitude: locationDetails.longitude,
        warehouse_type: costcoDataEntry.warehouse_type || 'Warehouse',
        membership_required: costcoDataEntry.membership_required ?? true,
        pharmacy_available: costcoDataEntry.pharmacy_available ?? false,
        optical_available: costcoDataEntry.optical_available ?? false,
        tyre_centre_available: costcoDataEntry.tyre_centre_available ?? false,
        capacities: locationDetails.capacities || {},
        hourly_business: costcoDataEntry.hourly_business || {},
        optimal_parking_locations: costcoDataEntry.optimal_parking_locations || '',
        notes: costcoDataEntry.notes || {
          restrictions: '',
          EV_charging: '',
          accessible_parking: '',
        },
        entrance_location: costcoDataEntry.entrance_location || '',
        checkout_peak_times: costcoDataEntry.checkout_peak_times || '',
        returns_desk_location: costcoDataEntry.returns_desk_location || '',
        specialty_sections: costcoDataEntry.specialty_sections || [],
        public_transport_access: costcoDataEntry.public_transport_access || '',
        fuel_station: costcoDataEntry.fuel_station || {
          coordinates: {
            latitude: locationDetails.latitude,
            longitude: locationDetails.longitude,
          },
          hours: '',
          notes: '',
        },
        parking_capacity: costcoDataEntry.parking_capacity || '',
        motorbike_parking: costcoDataEntry.motorbike_parking || '',
        food_court: costcoDataEntry.food_court || {
          location: '',
          hours: '',
          menu: [],
        },
        in_store_food_categories_location: costcoDataEntry.in_store_food_categories_location || {},
        trolley_location_tips: costcoDataEntry.trolley_location_tips || '',
        holiday_opening_hours: costcoDataEntry.holiday_opening_hours || {},
        special_events: costcoDataEntry.special_events || [],
        restock_tips: costcoDataEntry.restock_tips || '',
        hearing_aids_available: costcoDataEntry.hearing_aids_available ?? false,
        photo_centre: costcoDataEntry.photo_centre ?? false,
        click_and_collect: costcoDataEntry.click_and_collect ?? false,
        best_times_to_visit: costcoDataEntry.best_times_to_visit || {},
      };

      setCurrentCostco(currentCostco);
      setShowCostco(true);
      setRecommendationContext({
        name: shop ? String(shop) : location._id,
        coords: [locationDetails.latitude, locationDetails.longitude] as LatLngTuple,
      });
    } else {
      const enhancedLocation: Location = {
        _id: location._id,
        location: location._id,
        latitude: locationDetails.latitude,
        longitude: locationDetails.longitude,
        category: location.category as LocationCategory,
        busyness: Math.floor(Math.random() * 100),
        vibeRating: 3.5 + Math.random() * 1.5, // TODO: get rid
        communityRecommendations: [],
        parkingOptions: locationDetails.parkingOptions || [],
        capacities: locationDetails.capacities,
        shops: locationDetails.shops,
      };
      setCurrentDetails(enhancedLocation);
      setShowLocation(true);
      setRecommendationContext({
        name: shop ? String(shop) : enhancedLocation.location,
        coords: [enhancedLocation.latitude, enhancedLocation.longitude] as LatLngTuple,
      });
    }

    // Center the map on the selected location with smooth animation
    centerMapOnLocation(locationDetails.latitude, locationDetails.longitude, 16);
  };

  const resolveLocationById = useCallback(
    (locationId: string) => {
      if (!allLocations) return null;

      if (Array.isArray(allLocations)) {
        return allLocations.find((loc: any) => loc?._id === locationId || loc?.location === locationId) || null;
      }

      if (typeof allLocations === 'object' && allLocations !== null) {
        return (allLocations as any)[locationId] || null;
      }

      return null;
    },
    [allLocations]
  );

  const handleMyVibeLocationSelect = useCallback(
    (locationId: string) => {
      const locationData = resolveLocationById(locationId);
      if (!locationData) {
        console.warn('MyVibe: unable to resolve location for id', locationId);
        return;
      }

      void handleMarkerClick({ _id: locationId, ...locationData });
    },
    [resolveLocationById, handleMarkerClick]
  );

  const handleMyVibeShopSelect = useCallback(
    (locationId: string, shopId: string) => {
      const locationData = resolveLocationById(locationId);
      if (!locationData) {
        console.warn('MyVibe: unable to resolve shop location for id', locationId);
        return;
      }

      void handleMarkerClick({ _id: locationId, ...locationData }, shopId);
    },
    [resolveLocationById, handleMarkerClick]
  );

  const handleParkAndRideClick = async (location: ParkAndRideLocation) => {
    try {
      // Get fresh details for the selected location
      const details = await getParkAndRideDetails(location.facility_id);

      if (details) {
        setCurrentParkAndRide(details);
        setShowParkAndRide(true);
        setRecommendationContext({
          name: String(details.facility_id || 'Park & Ride'),
          coords: [details.latitude, details.longitude] as LatLngTuple,
        });
      } else {
        // Fallback to the original location data
        setCurrentParkAndRide(location);
        setShowParkAndRide(true);
        setRecommendationContext({
          name: String(location.facility_id || 'Park & Ride'),
          coords: [location.latitude, location.longitude] as LatLngTuple,
        });
      }

      // Center the map on the selected Park and Ride location with smooth animation
      centerMapOnLocation(location.latitude, location.longitude, 16);
    } catch (error) {
      console.error('❌ Error handling Park and Ride click:', error);
      // Fallback to the original location data
      setCurrentParkAndRide(location);
      setShowParkAndRide(true);

      // Center the map on the selected Park and Ride location with smooth animation
      centerMapOnLocation(location.latitude, location.longitude, 16);
    }
  };

  const handleBicycleParkingClick = async (location: BicycleParkingLocation) => {
    try {
      // Get fresh details for the selected location
      const details = await getBicycleParkingDetails(location.id);

      if (details) {
        setCurrentBicycleParking(details);
        setShowBicycleParking(true);
        setRecommendationContext({
          name: String(details.id || 'Bicycle Parking'),
          coords: [details.latitude, details.longitude] as LatLngTuple,
        });
      } else {
        // Fallback to the original location data
        setCurrentBicycleParking(location);
        setShowBicycleParking(true);
        setRecommendationContext({
          name: String(location.id || 'Bicycle Parking'),
          coords: [location.latitude, location.longitude] as LatLngTuple,
        });
      }

      // Center the map on the selected Bicycle Parking location with smooth animation
      centerMapOnLocation(location.latitude, location.longitude, 16);
    } catch (error) {
      console.error('❌ Error handling Bicycle Parking click:', error);
      // Fallback to the original location data
      setCurrentBicycleParking(location);
      setShowBicycleParking(true);

      // Center the map on the selected Bicycle Parking location with smooth animation
      centerMapOnLocation(location.latitude, location.longitude, 16);
    }
  };

  const handleMobilityParkingClick = async (location: MobilityParkingLocation) => {
    try {
      // Get fresh details for the selected location
      const details = await getMobilityParkingDetails(location.objectId);

      if (details) {
        setCurrentMobilityParking(details);
        setShowMobilityParking(true);
        setRecommendationContext({
          name: String(details.objectId || 'Mobility Parking'),
          coords: [details.latitude, details.longitude] as LatLngTuple,
        });
      } else {
        // Fallback to the original location data
        setCurrentMobilityParking(location);
        setShowMobilityParking(true);
        setRecommendationContext({
          name: String(location.objectId || 'Mobility Parking'),
          coords: [location.latitude, location.longitude] as LatLngTuple,
        });
      }

      // Center the map on the selected Mobility Parking location with smooth animation
      centerMapOnLocation(location.latitude, location.longitude, 16);
    } catch (error) {
      console.error('❌ Error handling Mobility Parking click:', error);
      // Fallback to the original location data
      setCurrentMobilityParking(location);
      setShowMobilityParking(true);

      // Center the map on the selected Mobility Parking location with smooth animation
      centerMapOnLocation(location.latitude, location.longitude, 16);
    }
  };

  const handleCarShareBayOperatorClick = async (location: CarShareBayOperatorLocation) => {
    try {
      // Get fresh details for the selected location
      const details = await getCarShareBayOperatorDetails(location.objectId);

      if (details) {
        setCurrentCarShareBayOperator(details);
        setShowCarShareBayOperator(true);
        setRecommendationContext({
          name: String(details.objectId || 'Car Share'),
          coords: [details.latitude, details.longitude] as LatLngTuple,
        });
      } else {
        console.error('❌ Failed to load Car Share Bay Operator details');
        // Fallback to the original location data
        setCurrentCarShareBayOperator(location);
        setShowCarShareBayOperator(true);
        setRecommendationContext({
          name: String(location.objectId || 'Car Share'),
          coords: [location.latitude, location.longitude] as LatLngTuple,
        });
      }

      // Center the map on the selected Car Share Bay Operator location with smooth animation
      centerMapOnLocation(location.latitude, location.longitude, 16);
    } catch (error) {
      console.error('❌ Error handling Car Share Bay Operator click:', error);
      // Fallback to the original location data
      setCurrentCarShareBayOperator(location);
      setShowCarShareBayOperator(true);

      // Center the map on the selected Car Share Bay Operator location with smooth animation
      centerMapOnLocation(location.latitude, location.longitude, 16);
    }
  };

  const handleSearchSubmission = (locationName: string, details = null) => {
    if (!locationName || typeof locationName !== 'string') {
      console.warn('Invalid location name passed to handleSearchSubmission:', locationName);
      return;
    }

    const location = allSearchLocations.find((name) => name.toLowerCase() === locationName.toLowerCase());
    const shopSplitter = locationName.split(` ::: `);
    if (shopSplitter.length === 2) {
      // This is a shop search
      handleMarkerClick({ _id: shopSplitter[0] }, shopSplitter[1], details);
    } else if (location) handleMarkerClick({ _id: location, ...allLocations[location] }, false, details);
  };

  // Helper function to calculate bounds for multiple locations
  const calculateBounds = (locations: Array<{ latitude: number; longitude: number }>) => {
    if (locations.length === 0) return null;

    let minLat = locations[0].latitude;
    let maxLat = locations[0].latitude;
    let minLng = locations[0].longitude;
    let maxLng = locations[0].longitude;

    locations.forEach((location) => {
      minLat = Math.min(minLat, location.latitude);
      maxLat = Math.max(maxLat, location.latitude);
      minLng = Math.min(minLng, location.longitude);
      maxLng = Math.max(maxLng, location.longitude);
    });

    return { minLat, maxLat, minLng, maxLng };
  };

  // Helper function to calculate center point
  const calculateCenter = (
    bounds: {
      minLat: number;
      maxLat: number;
      minLng: number;
      maxLng: number;
    } | null
  ): LatLngTuple => {
    if (!bounds) return [-33.8688, 151.2093]; // Default to Sydney CBD

    const centerLat = (bounds.minLat + bounds.maxLat) / 2;
    const centerLng = (bounds.minLng + bounds.maxLng) / 2;

    return [centerLat, centerLng];
  };

  // Current location functionality
  const getCurrentLocation = (): Promise<LatLngTuple> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      // Check if we're on HTTPS or localhost (required for geolocation on mobile)
      const isSecureContext = window.isSecureContext || window.location.hostname === 'localhost';
      if (!isSecureContext) {
        reject(new Error('Geolocation requires HTTPS or localhost'));
        return;
      }

      setIsLocating(true);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location: LatLngTuple = [latitude, longitude];
          setUserLocation(location);
          createUserLocationMarker(location);
          setIsLocating(false);
          resolve(location);
        },
        (error) => {
          setIsLocating(false);
          console.error('❌ Error getting current location:', error);

          // Provide more specific error messages for mobile
          let errorMessage = 'Unable to get your current location.';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your GPS settings.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage =
                'Unable to get your current location. Please check your location permissions and try again.';
          }

          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // Increased timeout for mobile
          maximumAge: 30000, // Reduced cache time for better accuracy
        }
      );
    });
  };

  const centerOnCurrentLocation = async () => {
    try {
      // Check if geolocation permission is already granted
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state === 'denied') {
          throw new Error('Location access denied. Please enable location permissions in your browser settings.');
        }
      }

      const location = await getCurrentLocation();
      // Center the map on the current location
      if (mapRef.current) {
        mapRef.current.setView(location, 16, {
          animate: true,
          duration: 0.8,
        });
      } else {
        // Fallback to state update
        setMapCenter(location);
        setMapZoom(16);
      }

      // Show a brief success message
      const mapElement = document.querySelector('.leaflet-container');
      if (mapElement) {
        mapElement.classList.add('location-found');
        setTimeout(() => mapElement.classList.remove('location-found'), 2000);
      }
    } catch (error) {
      console.error('❌ Failed to get current location:', error);

      // Show specific error message to user
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to get your current location. Please check your location permissions and try again.';

      // Use a more user-friendly notification instead of alert
      const notification = document.createElement('div');
      notification.className = 'location-error-notification';
      notification.innerHTML = `
        <div class="notification-content">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>${errorMessage}</span>
        </div>
      `;

      document.body.appendChild(notification);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 5000);
    }
  };

  // On application start: prompt for current location (same as compare locations flow)
  useEffect(() => {
    if (hasPromptedForLocationRef.current) return;
    hasPromptedForLocationRef.current = true;
    if (!navigator.geolocation) return;
    const isSecureContext = window.isSecureContext || window.location.hostname === 'localhost';
    if (!isSecureContext) return;
    getCurrentLocation().catch(() => {
      // User denied or error - silent; components can call requestLocation again
    });
  }, []);

  // Stable callback for children to request location (triggers browser prompt)
  const requestLocation = useCallback(
    (): Promise<[number, number]> => getCurrentLocation().then((loc) => [loc[0], loc[1]]),
    []
  );

  // Helper function to calculate optimal zoom level
  const calculateOptimalZoom = (
    bounds: {
      minLat: number;
      maxLat: number;
      minLng: number;
      maxLng: number;
    } | null
  ) => {
    if (!bounds) return 12; // Default zoom

    const latDiff = bounds.maxLat - bounds.minLat;
    const lngDiff = bounds.maxLng - bounds.minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    // Calculate zoom based on the difference
    if (maxDiff > 0.1) return 10; // Very spread out
    if (maxDiff > 0.05) return 11; // Spread out
    if (maxDiff > 0.02) return 12; // Medium spread
    if (maxDiff > 0.01) return 13; // Close together
    if (maxDiff > 0.005) return 14; // Very close
    return 15; // Extremely close
  };

  // Helper function to center map on location with smooth animation
  const centerMapOnLocation = (latitude: number, longitude: number, zoom: number = 16) => {
    // Add a visual indicator that the map is centering
    const mapElement = document.querySelector('.leaflet-container');
    if (mapElement) {
      mapElement.classList.add('map-centering');
      setTimeout(() => mapElement.classList.remove('map-centering'), 1000);
    }

    // Small delay to ensure map is fully initialized
    setTimeout(() => {
      // Use the actual map instance if available
      if (mapRef.current) {
        try {
          mapRef.current.setView([latitude, longitude], zoom, {
            animate: true,
            duration: 0.8,
          });
        } catch (error) {
          console.error('❌ Error centering map:', error);
          // Fallback to state update
          setMapCenter([latitude, longitude] as LatLngTuple);
          setMapZoom(zoom);
        }
      } else {
        // Fallback to state update
        setMapCenter([latitude, longitude] as LatLngTuple);
        setMapZoom(zoom);
      }
    }, 100);
  };

  // Helper function to center map on multiple locations with bounds calculation
  const centerMapOnMultipleLocations = (locations: Array<{ latitude: number; longitude: number }>) => {
    if (locations.length === 0) return;

    // Add a visual indicator that the map is centering
    const mapElement = document.querySelector('.leaflet-container');
    if (mapElement) {
      mapElement.classList.add('map-centering');
      setTimeout(() => mapElement.classList.remove('map-centering'), 1000);
    }

    if (locations.length === 1) {
      // Single location: center on it with close zoom
      const location = locations[0];
      centerMapOnLocation(location.latitude, location.longitude, 16);
    } else {
      // Multiple locations: calculate bounds and center with appropriate zoom
      const bounds = calculateBounds(locations);
      const center = calculateCenter(bounds);
      const zoom = calculateOptimalZoom(bounds);

      // Small delay to ensure map is fully initialized
      setTimeout(() => {
        // Use the actual map instance if available
        if (mapRef.current) {
          try {
            mapRef.current.setView(center, zoom, {
              animate: true,
              duration: 0.8,
            });
          } catch (error) {
            console.error('❌ Error centering map on multiple locations:', error);
            // Fallback to state update
            setMapCenter(center);
            setMapZoom(zoom);
          }
        } else {
          // Fallback to state update
          setMapCenter(center);
          setMapZoom(zoom);
        }
      }, 100);
    }
  };

  // const resetMapView = () => {
  //   setSelectedCategory(null);
  //   createAllMarkers(allLocations, "basic", "#3B82F6", handleMarkerClick, setAllMarkers);
  //   createAllMarkers(parkAndRideLocations, "pnr", "#3B82F6", handleParkAndRideClick, setParkAndRideMarkers);
  //   // Keep user location marker if available
  //   setMapCenter([-33.8688, 151.2093]);
  //   setMapZoom(12);
  // };

  const handleCategoryFilter = (category: LocationCategory | null) => {
    setSelectedCategory(category);
    if (category) {
      if (category === 'Park and Ride') {
        // Show only Park and Ride locations
        setAllMarkers([]);
        createAllMarkers(parkAndRideLocations, 'pnr', '#7c3aed', handleParkAndRideClick, setParkAndRideMarkers);
      } else if (category === 'Bicycle Parking') {
        // Show only Bicycle Parking locations
        setAllMarkers([]);
        createAllMarkers(
          bicycleParkingLocations,
          'bicycle',
          'linear-gradient(135deg, #4CAF50, #45a049)',
          handleBicycleParkingClick,
          setBicycleParkingMarkers
        );
      } else if (category === 'Mobility Parking') {
        // Show only Mobility Parking locations
        setAllMarkers([]);
        createAllMarkers(
          mobilityParkingLocations,
          'mobility',
          'linear-gradient(135deg, #3B82F6, #2563EB)',
          handleMobilityParkingClick,
          setMobilityParkingMarkers
        );
      } else if (category === 'Car Share Bay Operator') {
        // Show only Car Share Bay Operator locations
        setAllMarkers([]);
        createAllMarkers(
          carShareBayOperatorLocations,
          'carshare',
          'linear-gradient(135deg, #9C27B0, #7B1FA2)',
          handleCarShareBayOperatorClick,
          setCarShareBayOperatorMarkers
        );
      } else {
        // Show only regular locations of the selected category
        const filteredLocations = []; //allLocations.filter(loc => loc.category === category);
        createAllMarkers(filteredLocations, 'basic', '#7c3aed', handleMarkerClick, setAllMarkers);
      }
    } else {
      // Show regular locations and park & ride, but hide bicycle/mobility parking
      createAllMarkers(parkAndRideLocations, 'pnr', '#3B82F6', handleParkAndRideClick, setParkAndRideMarkers);
    }
  };

  const [selectedRecommendationLocation, setSelectedRecommendationLocation] = useState<LatLngTuple | null>(null);
  const [selectedRecommendationLocationLabel, setSelectedRecommendationLocationLabel] = useState<string | null>(null);
  const [selectedRecommendationLocationMeta, setSelectedRecommendationLocationMeta] = useState<{
    source?: RecommendationLocationSource;
    hasCoordinates?: boolean;
    isCurrentPlace?: boolean;
  } | null>(null);

  const toCoordinateObject = (coords?: LatLngTuple | null): { lat: number; lng: number } | null => {
    if (!coords || coords.length !== 2) return null;
    const [lat, lng] = coords;
    if (typeof lat !== 'number' || typeof lng !== 'number' || Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    return { lat, lng };
  };

  const toCoordinateObjectFromNumbers = (
    lat?: number | null,
    lng?: number | null
  ): { lat: number; lng: number } | null => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const generateRecommendationId = () => {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch (error) {
      console.warn('Unable to use crypto.randomUUID, falling back to timestamp id', error);
    }
    return `rec_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const createStandardRecommendationRecord = useCallback(
    (
      type: RecommendationType,
      payload: Record<string, unknown>,
      metadataOverrides?: Record<string, unknown>
    ): RecommendationRecord => {
      const id = generateRecommendationId();
      const coordinates = toCoordinateObject(selectedRecommendationLocation ?? recommendationContext?.coords ?? null);
      const label = selectedRecommendationLocationLabel ?? recommendationContext?.name ?? null;
      const source =
        selectedRecommendationLocationMeta?.source ?? (recommendationContext?.name ? 'modal-context' : undefined);

      const location: RecommendationRecord['location'] = {
        label,
        coordinates,
        ...(source ? { source } : {}),
      };

      const metadata: RecommendationRecord['metadata'] = {
        submittedAt: new Date().toISOString(),
        ...(metadataOverrides || {}),
      };

      if (selectedRecommendationLocationMeta) {
        const locationMetaEntries = Object.entries({
          source: selectedRecommendationLocationMeta.source,
          hasCoordinates: selectedRecommendationLocationMeta.hasCoordinates,
          isCurrentPlace: selectedRecommendationLocationMeta.isCurrentPlace,
        }).filter(([, value]) => value !== undefined && value !== null);

        if (locationMetaEntries.length) {
          metadata.locationMeta = Object.fromEntries(locationMetaEntries);
        }
      }

      if (recommendationContext?.name) {
        metadata.contextName = recommendationContext.name;
      }

      return {
        id,
        type,
        location,
        payload,
        metadata,
      };
    },
    [
      recommendationContext,
      selectedRecommendationLocation,
      selectedRecommendationLocationLabel,
      selectedRecommendationLocationMeta,
    ]
  );

  const persistRecommendationRecord = useCallback(async (record: RecommendationRecord) => {
    try {
      await testRecommendation(`${record.location.label}`, record); // shouldn't we store in categories too? i.e. QVB/parking, etc?
      // Trigger glow effect on successful submission
      setShowRecommendationGlow(true);
      setTimeout(() => {
        setShowRecommendationGlow(false);
      }, 1800); // Match animation duration
    } catch (error) {
      console.error('Failed to write recommendation', record, error);
    }
  }, []);

  const resetRecommendationSelection = useCallback(() => {
    setSelectedRecommendationCategory(null);
    setSelectedRecommendationLocation(null);
    setSelectedRecommendationLocationLabel(null);
    setSelectedRecommendationLocationMeta(null);
  }, []);

  const handleRecommendationCategorySelect = (
    category: 'parking' | 'coffee' | 'crowd' | 'vibe' | 'beer',
    locationCoords?: LatLngTuple | null,
    locationLabel?: string | null,
    locationMeta?: {
      source?: RecommendationLocationSource;
      hasCoordinates?: boolean;
      isCurrentPlace?: boolean;
    }
  ) => {
    // console.log('🎯 Category selected:', category, 'with coords:', locationCoords, 'label:', locationLabel);
    setSelectedRecommendationCategory(category);
    setSelectedRecommendationLocation(locationCoords || null);
    setSelectedRecommendationLocationLabel(locationLabel || null);
    setSelectedRecommendationLocationMeta(locationMeta || null);
    if (category === 'parking') {
      setShowAddParkingModal(true);
    } else if (category === 'crowd') {
      setShowCrowdModal(true);
    } else if (category === 'vibe') {
      setShowVibeModal(true);
    } else if (category === 'beer') {
      setShowBeerModal(true);
    } else {
      setShowCoffeeModal(true);
    }
  };

  const handleParkingRecommendationSubmit = (data: {
    category: 'street' | 'car-park' | 'private' | 'other';
    pricing: 'free' | 'paid';
    details?: {
      price?: string;
      paymentMethod?: string;
      meteredTimes?: string;
      difficultyLevel?: number;
      accessibleParking?: boolean;
      evCharging?: boolean;
      heightRestrictions?: string;
      notes?: string;
    };
    recommendedLocation?: {
      id: string;
      name?: string;
      displayName?: string;
      address?: string;
      latitude?: number;
      longitude?: number;
      distance?: number;
      source: 'local' | 'geoapify';
      isVibeCheckSupported: boolean;
    } | null;
    referenceLocation?: {
      label?: string | null;
      coordinates?: LatLngTuple | null;
    } | null;
  }) => {
    // console.log('New parking recommendation:', data);

    const baseRecord = createStandardRecommendationRecord(
      'parking',
      {
        category: data.category,
        pricing: data.pricing,
        details: data.details ?? {},
      },
      {
        steps: ['category', 'location', 'pricing', 'details'],
      }
    );

    const contextLabel = baseRecord.location.label;
    const contextCoordinates = baseRecord.location.coordinates;

    const recommendedLabel =
      data.recommendedLocation?.displayName ??
      data.recommendedLocation?.name ??
      data.recommendedLocation?.id ??
      contextLabel ??
      selectedRecommendationLocationLabel ??
      recommendationContext?.name ??
      null;

    const recommendedCoordinatesFromResult = toCoordinateObjectFromNumbers(
      data.recommendedLocation?.latitude ?? null,
      data.recommendedLocation?.longitude ?? null
    );

    const recommendedCoordinates =
      recommendedCoordinatesFromResult ??
      contextCoordinates ??
      toCoordinateObject(selectedRecommendationLocation) ??
      toCoordinateObject(recommendationContext?.coords ?? null);

    const recommendingForLabel =
      data.referenceLocation?.label ??
      contextLabel ??
      selectedRecommendationLocationLabel ??
      recommendationContext?.name ??
      null;

    const recommendingForCoordinates =
      toCoordinateObject(data.referenceLocation?.coordinates ?? null) ??
      contextCoordinates ??
      toCoordinateObject(selectedRecommendationLocation) ??
      toCoordinateObject(recommendationContext?.coords ?? null);

    const record: RecommendationRecord = {
      ...baseRecord,
      location: {
        label: recommendedLabel,
        coordinates: recommendedCoordinates,
        ...(baseRecord.location.source ? { source: baseRecord.location.source } : {}),
      },
      recommendationContext: {
        recommendingFor: {
          label: recommendingForLabel,
          coordinates: recommendingForCoordinates,
        },
        recommendedLocation: data.recommendedLocation
          ? {
              label: recommendedLabel,
              address: data.recommendedLocation.address ?? null,
              coordinates: recommendedCoordinatesFromResult ?? recommendedCoordinates,
              distanceMeters:
                typeof data.recommendedLocation.distance === 'number' ? data.recommendedLocation.distance : null,
              source: data.recommendedLocation.source,
              isVibeCheckSupported: data.recommendedLocation.isVibeCheckSupported,
            }
          : undefined,
      },
    };

    void persistRecommendationRecord(record);
  };

  const handleTabChange = (tab: string) => {
    // Update active tab immediately for responsive UI
    setActiveTab(tab);

    // Defer heavy operations to avoid blocking UI
    if (tab === 'home') {
      // Use requestAnimationFrame to defer heavy map operations
      requestAnimationFrame(() => {
        // Reset map to show locations based on current category filter
        if (selectedCategory === 'Park and Ride') {
          setAllMarkers([]);
          createAllMarkers(parkAndRideLocations, 'pnr', '#7c3aed', handleParkAndRideClick, setParkAndRideMarkers);
        } else if (selectedCategory === 'Bicycle Parking') {
          setAllMarkers([]);
          createAllMarkers(
            bicycleParkingLocations,
            'bicycle',
            'linear-gradient(135deg, #4CAF50, #45a049)',
            handleBicycleParkingClick,
            setBicycleParkingMarkers
          );
        } else if (selectedCategory === 'Mobility Parking') {
          setAllMarkers([]);
          createAllMarkers(
            mobilityParkingLocations,
            'mobility',
            'linear-gradient(135deg, #3B82F6, #2563EB)',
            handleMobilityParkingClick,
            setMobilityParkingMarkers
          );
        } else if (selectedCategory === 'Car Share Bay Operator') {
          setAllMarkers([]);
          createAllMarkers(
            carShareBayOperatorLocations,
            'carshare',
            'linear-gradient(135deg, #9C27B0, #7B1FA2)',
            handleCarShareBayOperatorClick,
            setCarShareBayOperatorMarkers
          );
        } else if (selectedCategory) {
          // // Show only regular locations of the selected category
          // const filteredLocations = []; //allLocations.filter(loc => loc.category === selectedCategory);
          // setAllMarkers([]);
          // createAllMarkers(filteredLocations, 'basic', '#7c3aed', handleMarkerClick, setAllMarkers);
        } else {
          // Show regular locations (no special categories)
          createAllMarkers(allLocations as any, 'basic', '#7c3aed', handleMarkerClick, setAllMarkers);
        }
        setMapCenter([-33.8688, 151.2093]);
        setMapZoom(12);
      });
    }
  };

  const [brandReferenceLocation, setBrandReferenceLocation] = useState<[number, number] | null>(null);

  const handleBrandClickFromHome2 = (
    brand: string,
    locations: Array<Location & { distance?: number }>,
    referenceLocation: [number, number] | null
  ) => {
    setSelectedBrand(brand);
    setSelectedBrandLocations(locations);
    setBrandReferenceLocation(referenceLocation);
    setShowBrandLocations(true);
  };

  const handleHome2LocationChange = useCallback((location: [number, number] | null) => {
    setHome2ReferenceLocation(location);
  }, []);

  const handleLocationClickFromHome2 = async (location: Location) => {
    // Call handleMarkerClick directly with the location object
    // Pass null as details to ensure it fetches full location data including busyness
    // This matches how the search dropdown works - it fetches fresh data from the API
    const locationId = location._id || location.location || '';

    // Create a location object in the format handleMarkerClick expects
    const locationForMarker = {
      _id: locationId,
      category: location.category,
      ...location,
    };

    // Call handleMarkerClick with null details to force API fetch for busyness data
    await handleMarkerClick(locationForMarker, null, null);
  };

  const handleCoffeeLocationClickFromHome2 = async (location: any) => {
    // Switch to coffee tab first
    setActiveTab('coffee');
    // Update reference location so CoffeeMapper2 can center on it
    if (location.latitude && location.longitude) {
      setHome2ReferenceLocation([location.latitude, location.longitude]);
    }
    // Set location to open - CoffeeMapper2 will automatically center, zoom, and open the modal
    // Use a small delay to ensure the tab switch and component mount completes
    setTimeout(() => {
      setCoffeeLocationToOpen(location);
    }, 100);
  };

  return (
    <div className="App">
      {/* Recommendation Success Glow Effect */}
      {showRecommendationGlow && <div className="recommendation-glow-overlay" />}

      {/* Brands List View */}
      {showBrandsList ? (
        <BrandsListView
          allLocations={allLocations}
          userLocation={userLocation ? [userLocation[0], userLocation[1]] : null}
          referenceLocation={brandsListReferenceLocation}
          onBack={() => {
            setShowBrandsList(false);
            setBrandsListReferenceLocation(null);
          }}
          onBrandClick={(brand, locations, referenceLocation) => {
            setSelectedBrand(brand);
            setSelectedBrandLocations(locations);
            setBrandReferenceLocation(referenceLocation);
            setShowBrandsList(false);
            setShowBrandLocations(true);
          }}
        />
      ) : showLocationsList ? (
        <LocationsListView
          title={
            locationsListType === 'shopping'
              ? 'Shopping Centres'
              : locationsListType === 'beaches'
                ? 'Beaches'
                : 'Coffee Locations'
          }
          locations={locationsListData}
          allLocations={allLocations}
          userLocation={userLocation ? [userLocation[0], userLocation[1]] : null}
          referenceLocation={locationsListReferenceLocation}
          categoryFilter={locationsListType === 'beaches' ? 'Beaches' : undefined}
          onBack={() => {
            setShowLocationsList(false);
            setLocationsListData([]);
            setLocationsListReferenceLocation(null);
          }}
          onLocationClick={handleLocationClickFromHome2}
        />
      ) : showBrandLocations ? (
        <BrandLocationsView
          brand={selectedBrand}
          locations={selectedBrandLocations}
          allLocations={allLocations}
          userLocation={userLocation ? [userLocation[0], userLocation[1]] : null}
          referenceLocation={brandReferenceLocation}
          onBack={() => {
            setShowBrandLocations(false);
            setBrandReferenceLocation(null);
          }}
          onLocationClick={handleLocationClickFromHome2}
          onShopClick={(locationName, shopName, location) => {
            // Handle shop click - same as search dropdown
            handleSearchSubmission(`${locationName} ::: ${shopName}`);
          }}
        />
      ) : showAllSearch ? (
        <AllSearch
          options={allSearchLocations}
          allLocations={allLocations}
          allCoffeeLocations={allCoffeeLocations}
          userLocation={userLocation ? [userLocation[0], userLocation[1]] : null}
          referenceLocation={home2ReferenceLocation}
          onRequestLocation={requestLocation}
          isLocating={isLocating}
          onBack={() => {
            setShowAllSearch(false);
            // Don't clear callback here - LocationComparer2 might still be open
          }}
          onCoffeeSelect={(cafe) => {
            setShowAllSearch(false);
            setActiveTab('coffee');
            if (cafe.latitude != null && cafe.longitude != null) {
              setHome2ReferenceLocation([cafe.latitude, cafe.longitude]);
            }
            setCoffeeLocationToOpen(cafe);
          }}
          onSelect={async (locationName) => {
            // If opened from LocationComparer2, add to comparer and keep AllSearch open
            if (addToComparerCallback) {
              try {
                await addToComparerCallback(locationName);
                // Keep AllSearch open - don't close it
              } catch (error) {
                console.error('Error adding location to comparer:', error);
              }
            } else {
              handleSearchSubmission(locationName);
              setShowAllSearch(false);
            }
          }}
          onCategoryFilter={handleCategoryFilter}
          onParkAndRideSelect={async (locationName) => {
            if (addToComparerCallback) {
              await addToComparerCallback(locationName);
            } else {
              handleSearchSubmission(locationName);
              setShowAllSearch(false);
            }
          }}
          onBicycleParkingSelect={async (locationName) => {
            if (addToComparerCallback) {
              await addToComparerCallback(locationName);
            } else {
              handleSearchSubmission(locationName);
              setShowAllSearch(false);
            }
          }}
          onMobilityParkingSelect={async (locationName) => {
            if (addToComparerCallback) {
              await addToComparerCallback(locationName);
            } else {
              handleSearchSubmission(locationName);
              setShowAllSearch(false);
            }
          }}
          onCarShareBayOperatorSelect={async (locationName) => {
            if (addToComparerCallback) {
              await addToComparerCallback(locationName);
            } else {
              handleSearchSubmission(locationName);
              setShowAllSearch(false);
            }
          }}
        />
      ) : activeTab === 'home2' ? (
        <Home2
          allLocations={allLocations}
          allCoffeeLocations={allCoffeeLocations}
          preprocessedCoffeeLocations={preprocessedCoffeeLocations}
          userLocation={userLocation ? [userLocation[0], userLocation[1]] : null}
          onRequestLocation={requestLocation}
          isLocating={isLocating}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onMyVibeClick={() => setShowMyVibe(true)}
          onLocationClick={handleLocationClickFromHome2}
          hideNavbar={shouldHideNavbar}
          onCoffeeLocationClick={handleCoffeeLocationClickFromHome2}
          onSearchClick={() => {
            setShowAllSearch(true);
          }}
          onBrandClick={handleBrandClickFromHome2}
          onShowAllClick={(type, locations, referenceLocation) => {
            // For coffee, switch to coffee tab instead of showing list view
            if (type === 'coffee') {
              setActiveTab('coffee');
              // Update reference location so CoffeeMapper2 can use it
              if (referenceLocation) {
                setHome2ReferenceLocation(referenceLocation);
              }
            } else {
              // For shopping and beaches, show list view as before
              setLocationsListType(type);
              setLocationsListData(locations);
              setLocationsListReferenceLocation(referenceLocation);
              setShowLocationsList(true);
            }
          }}
          onShowAllBrandsClick={(referenceLocation) => {
            setBrandsListReferenceLocation(referenceLocation);
            setShowBrandsList(true);
          }}
          onLocationChange={handleHome2LocationChange}
        />
      ) : activeTab === 'coffee' ? (
        <CoffeeMapper2
          referenceLocation={home2ReferenceLocation}
          userLocation={userLocation ? [userLocation[0], userLocation[1]] : null}
          onRequestLocation={requestLocation}
          isLocating={isLocating}
          allCoffeeLocations={allCoffeeLocations}
          preprocessedCoffeeLocations={preprocessedCoffeeLocations}
          locationToOpen={coffeeLocationToOpen}
          onLocationOpened={() => setCoffeeLocationToOpen(null)}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onMyVibeClick={() => setShowMyVibe(true)}
          hideNavbar={shouldHideNavbar}
        />
      ) : activeTab === 'home' ? (
        <MapPage
          mapCenter={mapCenter}
          mapZoom={mapZoom}
          markers={[
            ...allMarkers,
            ...parkAndRideMarkers,
            ...bicycleParkingMarkers,
            ...mobilityParkingMarkers,
            ...carShareBayOperatorMarkers,
            ...costcoMarkers,
            userLocationMarker,
          ].filter(Boolean)}
          onLocationSelect={handleMarkerClick}
          onSearchClick={() => setShowAllSearch(true)}
          onCompareClick={() => setShowLocationComparer2(true)}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onMyVibeClick={() => setShowMyVibe(true)}
          hideNavbar={shouldHideNavbar}
          userLocation={userLocation}
          isLocating={isLocating}
          onRequestLocation={requestLocation}
          centerOnCurrentLocation={centerOnCurrentLocation}
        />
      ) : activeTab === 'feedback' ? (
        <FeedbackPage
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onMyVibeClick={() => setShowMyVibe(true)}
          hideNavbar={shouldHideNavbar}
        />
      ) : (
        <>
          {showLocationComparer && (
            <LocationComparer
              shops={allSearchLocations}
              handleClose={() => setShowLocationComparer(false)}
              handleSearchSubmission={handleSearchSubmission}
            />
          )}
          {/* Keep LocationComparer2 mounted even when AllSearch is open so callback stays available */}
          {showLocationComparer2 && (
            <div style={{ display: showAllSearch ? 'none' : 'block' }}>
              <LocationComparer2
                shops={allSearchLocations}
                handleClose={() => {
                  setShowLocationComparer2(false);
                  setAddToComparerCallback(null);
                  // Close AllSearch if it's open
                  if (showAllSearch) {
                    setShowAllSearch(false);
                  }
                }}
                handleSearchSubmission={handleSearchSubmission}
                allLocations={allLocations}
                userLocation={userLocation ? [userLocation[0], userLocation[1]] : null}
                onRequestLocation={requestLocation}
                isLocating={isLocating}
                onSearchOverlayChange={setIsSearchOverlayActive}
                onOpenAllSearch={() => {
                  setShowAllSearch(true);
                }}
                isComparerContext={true}
                onAddToComparer={(callback) => {
                  if (callback) {
                    setAddToComparerCallback(() => callback);
                  } else {
                    setAddToComparerCallback(null);
                  }
                }}
              />
            </div>
          )}

          {/* Main Map Container - Fallback for other tabs */}
          <main className="map-container">
            {/* Glassmorphic VibeCheck Logo */}
            <div className="map-vc-logo-container">
              <div className="map-vc-logo">
                <span className="map-vc-text">VC</span>
              </div>
            </div>

            {/* Glassmorphic Compare Button */}
            <div className="map-compare-button-container">
              <button
                onClick={() => setShowLocationComparer2(true)}
                className="map-compare-button"
                title="Compare locations"
              >
                <GitCompare size={20} />
                <span>Compare</span>
              </button>
            </div>

            {/* Glassmorphic Search Button */}
            <div className="map-search-button-container">
              <button onClick={() => setShowAllSearch(true)} className="map-search-button" title="Search locations">
                <Search size={24} />
              </button>
            </div>

            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              zoomControl={false}
              className="map-container"
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                subdomains="abcd"
                maxZoom={20}
              />
              <MarkerClusterGroup
                chunkedLoading={true}
                chunkInterval={150}
                chunkDelay={100}
                removeOutsideVisibleBounds={true}
                disableClusteringAtZoom={16} // Slightly higher than CoffeeMapper2
                maxClusterRadius={60} // Slightly larger radius for main map
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
                zoomToBoundsOnClick={true}
                animate={false}
                animateAddingMarkers={false}
              >
                {allMarkers}
              </MarkerClusterGroup>
              {parkAndRideMarkers}
              {bicycleParkingMarkers}
              {mobilityParkingMarkers}
              {carShareBayOperatorMarkers}
              {costcoMarkers}
              {userLocationMarker}

              {/* Current Location Button */}
              <div className="current-location-button-container">
                <button
                  onClick={centerOnCurrentLocation}
                  disabled={isLocating}
                  className="current-location-button"
                  title="Center on my location"
                >
                  {isLocating ? (
                    <div className="location-spinner"></div>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                      <circle cx="12" cy="9" r="2.5" />
                    </svg>
                  )}
                </button>
              </div>

              <MapController onLocationSelect={handleMarkerClick} selectedCategory={selectedCategory} mapRef={mapRef} />
            </MapContainer>
          </main>
        </>
      )}

      {/* LocationComparer2 - Keep mounted even when AllSearch is open so callback stays available */}
      {showLocationComparer2 && (
        <div style={{ display: showAllSearch ? 'none' : 'block' }}>
          <LocationComparer2
            shops={allSearchLocations}
            handleClose={() => {
              setShowLocationComparer2(false);
              setAddToComparerCallback(null);
              // Close AllSearch if it's open
              if (showAllSearch) {
                setShowAllSearch(false);
              }
            }}
            handleSearchSubmission={handleSearchSubmission}
            allLocations={allLocations}
            userLocation={userLocation ? [userLocation[0], userLocation[1]] : null}
            onRequestLocation={requestLocation}
            isLocating={isLocating}
            onSearchOverlayChange={setIsSearchOverlayActive}
            onOpenAllSearch={() => {
              setShowAllSearch(true);
            }}
            isComparerContext={true}
            onAddToComparer={(callback) => {
              if (callback) {
                setAddToComparerCallback(() => callback);
              } else {
                setAddToComparerCallback(null);
              }
            }}
          />
        </div>
      )}

      {/* Modals - Always render regardless of active tab */}
      {showLocation && (
        <LocationModal
          details={{
            _id: currentDetails?._id,
            name: currentDetails?.location || '',
            details: currentDetails,
          }}
          handleClose={() => {
            setShowLocation(false);
            setCurrentDetails(null);
          }}
          shop={currentShop}
          onShopSelect={(shopData) => {
            // Convert to object format if string
            const shopObj = typeof shopData === 'string' ? { name: shopData } : shopData;
            setCurrentShop(shopObj);
            // Update recommendation context with shop name and coordinates
            if (shopObj && currentDetails) {
              const shopName = shopObj.name || '';
              const locationName = currentDetails.location || currentDetails._id || '';
              const formattedName = shopObj.centreName
                ? `${shopName} - ${shopObj.centreName}`
                : shopObj.name
                  ? `${shopName} - ${locationName}`
                  : shopName;

              const coords: LatLngTuple | null =
                shopObj.latitude !== undefined && shopObj.longitude !== undefined
                  ? [shopObj.latitude, shopObj.longitude]
                  : currentDetails.latitude && currentDetails.longitude
                    ? [currentDetails.latitude, currentDetails.longitude]
                    : null;

              setRecommendationContext({ name: formattedName, coords });
            }
          }}
        />
      )}

      {showBeach && currentBeach && (
        <BeachModal
          details={currentBeach}
          handleClose={() => {
            setShowBeach(false);
            setCurrentBeach(null);
          }}
        />
      )}

      {showParkAndRide && (
        <ParkAndRideModal
          details={currentParkAndRide}
          handleClose={() => {
            setShowParkAndRide(false);
            setCurrentParkAndRide(null);
          }}
        />
      )}
      {showBicycleParking && (
        <BicycleParkingPopup
          location={currentBicycleParking}
          onClose={() => {
            setShowBicycleParking(false);
            setCurrentBicycleParking(null);
          }}
        />
      )}
      {showMobilityParking && (
        <MobilityParkingPopup
          location={currentMobilityParking}
          onClose={() => {
            setShowMobilityParking(false);
            setCurrentMobilityParking(null);
          }}
        />
      )}
      {showCarShareBayOperator && (
        <CarShareBayOperatorPopup
          location={currentCarShareBayOperator}
          onClose={() => {
            setShowCarShareBayOperator(false);
            setCurrentCarShareBayOperator(null);
          }}
        />
      )}
      {/* {showShop && currentShop && (
        <ShopModal
          shop={currentShop}
          handleClose={() => {
            setShowShop(false);
            setCurrentShop(null);
          }}
        />
      )} */}
      {showCostco && currentCostco && (
        <CostcoModal
          location={currentCostco}
          handleClose={() => {
            setShowCostco(false);
            setCurrentCostco(null);
          }}
        />
      )}

      <MyVibeModal
        isOpen={showMyVibe}
        onClose={() => setShowMyVibe(false)}
        allLocations={allLocations}
        userLocation={userLocation}
        onSelectLocation={handleMyVibeLocationSelect}
        onSelectShop={handleMyVibeShopSelect}
      />

      {/* Recommendations FAB - DISABLED FOR NOW */}
      {/* {!(
        showAddParkingModal ||
        showCrowdModal ||
        showVibeModal ||
        showCoffeeModal ||
        showBeerModal ||
        showComingSoon ||
        showMyVibe ||
        isSearchOverlayActive
      ) && (
        <RecommendationsFAB
          onSelectCategory={handleRecommendationCategorySelect}
          currentLocation={userLocation}
          overridePlace={
            // If in a modal, use that location
            showLocation && currentDetails
              ? currentShop && currentShop.centreName
                ? `${currentShop.name} - ${currentShop.centreName}`
                : currentShop && currentShop.name
                ? `${currentShop.name} - ${currentDetails.location || currentDetails._id}`
                : currentShop?.name || currentDetails.location || null
              : showCostco && currentCostco
              ? currentCostco.name || null
              : null
          }
          overridePlaceCoords={
            // Pass coordinates from recommendation context or modal location
            recommendationContext?.coords || null
          }
          isInModal={showLocation || showCostco || !!currentShop}
          allLocations={allLocations}
        />
      )} */}

      {/* Add Parking Modal */}
      <AddParkingRecsModal
        isOpen={showAddParkingModal}
        onClose={() => {
          setShowAddParkingModal(false);
          resetRecommendationSelection();
        }}
        onSubmit={handleParkingRecommendationSubmit}
        allLocations={allLocations}
        currentLocation={userLocation}
        referenceLocation={selectedRecommendationLocation}
        referenceLocationName={selectedRecommendationLocationLabel}
      />

      {/* Crowd level selection */}
      <CrowdLevelModal
        isOpen={showCrowdModal}
        onClose={() => {
          setShowCrowdModal(false);
          resetRecommendationSelection();
        }}
        onSelect={(level) => {
          setSelectedCrowdLevel(level);
          const record = createStandardRecommendationRecord('crowd', { crowdLevel: level });
          void persistRecommendationRecord(record);
          setShowCrowdModal(false);
          resetRecommendationSelection();
          // console.log('Selected crowd level:', level);
        }}
      />

      {/* Coffee modal */}
      <CoffeePriceModal
        isOpen={showCoffeeModal}
        onClose={() => {
          setShowCoffeeModal(false);
          resetRecommendationSelection();
        }}
        onSubmit={(data) => {
          const record = createStandardRecommendationRecord(
            'coffee',
            {
              coffeeType: data.coffee,
              customCoffeeName: data.customCoffeeName ?? null,
              price: data.price,
              currency: 'AUD',
              qualityRating: data.quality,
              crowdLevel: data.crowd,
            },
            {
              steps: ['type', 'price-quality', 'crowd'],
            }
          );
          void persistRecommendationRecord(record);
          // console.log('Coffee submission:', data);
        }}
      />

      {/* Vibe level selection */}
      <VibeLevelModal
        isOpen={showVibeModal}
        onClose={() => {
          setShowVibeModal(false);
          resetRecommendationSelection();
        }}
        onSelect={(level) => {
          setSelectedVibeLevel(level);
          const record = createStandardRecommendationRecord('vibe', { vibeLevel: level });
          void persistRecommendationRecord(record);
          setShowVibeModal(false);
          resetRecommendationSelection();
          // console.log('Selected vibe level:', level);
        }}
        latitude={userLocation ? userLocation[0] : undefined}
        longitude={userLocation ? userLocation[1] : undefined}
      />

      {/* Beer Price Modal */}
      <BeerPriceModal
        isOpen={showBeerModal}
        onClose={() => {
          setShowBeerModal(false);
          resetRecommendationSelection();
        }}
        onSubmit={(data) => {
          const record = createStandardRecommendationRecord(
            'beer',
            {
              beerType: data.beer,
              customBeerName: data.customBeerName ?? null,
              size: data.size,
              price: data.price,
              currency: 'AUD',
              crowdLevel: data.crowd,
              hasHappyHour: data.hasHappyHour,
              happyHourRange: data.happyHourRange ?? null,
            },
            {
              steps: ['type', 'size', 'price', 'crowd'],
            }
          );
          void persistRecommendationRecord(record);
          // console.log('Beer submission:', data);
        }}
      />

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={showComingSoon}
        onClose={() => {
          setShowComingSoon(false);
          setSelectedRecommendationCategory(null);
        }}
        onFeedbackClick={() => {
          // Placeholder: integrate with Feedback modal if desired
          // console.log('Feedback clicked from ComingSoonModal');
        }}
      />

      {/* Bottom Navigation is now handled by AppShell in each page component */}
    </div>
  );
};

export default App;

type RecommendationType = 'crowd' | 'vibe' | 'coffee' | 'beer' | 'parking';
type RecommendationLocationSource = 'current-location' | 'search' | 'drop-pin' | 'modal-context' | 'unknown';

interface RecommendationRecord {
  id: string;
  type: RecommendationType;
  location: {
    label: string | null;
    coordinates: { lat: number; lng: number } | null;
    source?: RecommendationLocationSource;
  };
  payload: Record<string, unknown>;
  recommendationContext?: {
    recommendingFor?: {
      label: string | null;
      coordinates?: { lat: number; lng: number } | null;
    };
    recommendedLocation?: {
      label: string | null;
      address?: string | null;
      coordinates?: { lat: number; lng: number } | null;
      distanceMeters?: number | null;
      source?: string | null;
      isVibeCheckSupported?: boolean;
    };
  };
  metadata: {
    submittedAt: string;
    [key: string]: unknown;
  };
}
